import React from 'react';
import styled, { css } from 'styled-components';
import { Box, Heading, Paragraph } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces } from '@99designs/emu/settings';
import Media, { MediaProps } from './Media';

interface TwoColumnImageTextProps {
    children: React.ReactNode;
    variations: ['media-first' | 'copy-first'];
    backgroundColor: string;
    fontColor?: string;
    heading: string;
    subHeading: string;
    borderBottom: boolean;
    bottom: React.ReactNode;
    mediaProps: MediaProps;
}

export default function Index(props: TwoColumnImageTextProps) {
    return (
        <Box background={props.backgroundColor} color={props.fontColor}>
            <ContentSideFeatureContainer variations={props.variations}>
                <ContentSideFeatureSide variations={props.variations}>
                    <Media {...props.mediaProps} />
                </ContentSideFeatureSide>
                <ContentSideFeatureTop>
                    <Heading appearanceLevel={0} accessibilityLevel={1} withDivider>
                        {props.heading}
                    </Heading>
                </ContentSideFeatureTop>
                <ContentSideFeatureBottom>
                    <Box padding={['none', '4bu']}>
                        <Heading
                            appearanceLevel={2}
                            accessibilityLevel={2}
                            style={{ marginBottom: spaces.px['1.5bu'] }}
                        >
                            {props.subHeading}
                        </Heading>

                        <Box
                            style={props.borderBottom ? { borderBottom: '1px solid #000' } : {}}
                            paddingBottom={props.borderBottom ? '3bu' : 'none'}
                        >
                            <Paragraph color={props.fontColor}>{props.children}</Paragraph>
                        </Box>

                        <Box marginTop="1bu" marginBottom="2bu">
                            {props.bottom}
                        </Box>
                    </Box>
                </ContentSideFeatureBottom>
            </ContentSideFeatureContainer>
        </Box>
    );
}

const ContentSideFeatureContainer = styled.div<{ variations?: string[] }>`
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: 55% 45%;

    ${mediaQuery.minimum('large')} {
        grid-template-areas:
            'side-container .'
            'side-container top-container'
            'side-container bottom-container'
            'side-container .';

        ${({ variations }) =>
            variations?.includes('copy-first') &&
            css`
                grid-template-columns: 45% 55%;
                grid-template-areas:
                    '. side-container'
                    'top-container side-container'
                    'bottom-container side-container'
                    '. side-container';
            `}
    }

    ${mediaQuery.only('normal')} {
        grid-template-columns: 50% 50%;
        grid-template-areas:
            'top-container top-container'
            'side-container bottom-container'
            'side-container bottom-container';

        ${({ variations }) =>
            variations?.includes('copy-first') &&
            css`
                grid-template-areas:
                    'top-container top-container'
                    'bottom-container side-container'
                    'bottom-container side-container';
            `}
    }

    ${mediaQuery.between('small', 'medium')} {
        grid-template-columns: 1fr;
        grid-template-areas:
            'top-container'
            'side-container'
            'bottom-container';

        ${({ variations }) =>
            variations?.includes('media-first') &&
            css`
                grid-template-areas:
                    'side-container'
                    'top-container'
                    'bottom-container';
            `}

        ${({ variations }) =>
            variations?.includes('copy-first') &&
            css`
                grid-template-areas:
                    'top-container'
                    'bottom-container'
                    'side-container';
            `}
    }
`;

const ContentSideFeatureSide = styled.div<{ variations?: string[] }>`
    grid-area: side-container;
    text-align: right;
    padding-right: ${spaces['2bu']};

    ${({ variations }) =>
        (variations?.includes('media-first') || variations?.includes('copy-first')) &&
        css`
            padding-right: 0;
        `}

    ${mediaQuery.minimum('medium')} {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    ${mediaQuery.between('small', 'medium')} {
        padding-bottom: 60px;
    }
`;

const ContentSideFeatureTop = styled.div`
    grid-area: top-container;
`;

const ContentSideFeatureBottom = styled.div`
    grid-area: bottom-container;

    ${mediaQuery.only('medium')} {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
`;
